import { Alert, Skeleton } from "@hakistudio/hakiui";
import { Check, Copy, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import {
  connectCommand,
  MCP_CONNECTOR_URL,
  MCP_CLIENTS,
  MCP_CLIENT_LABELS,
  since,
  stateOf,
  useMcpStatus,
  useTauriConfigured,
  useTauriConnect,
  WRITABLE_CLIENTS,
  type ConnectionState,
  type McpClient,
} from "@/lib/mcp";
import { isTauri } from "@/lib/platform";
import { useProfile } from "@/lib/queries";

const DOT: Record<ConnectionState, string> = {
  live: "bg-emerald-500",
  stale: "bg-amber-500",
  never: "bg-ink-faint/40",
};

/**
 * Connect Claude and Cursor to these notes, and show whether it actually works.
 *
 * The status is a heartbeat the MCP server writes when it starts and while
 * tools are used — not a check that a config file exists. Those disagree often
 * enough to matter: a config surviving a moved repo, an upgraded `bun` or a
 * deleted profile still reads as configured while the server never starts.
 *
 * The desktop build can connect directly, because Rust can write the file and
 * the page cannot. In a browser the same action is a one-line command to paste,
 * which is the honest limit of what a web page is allowed to do to your editor.
 */
export function McpCard() {
  const { data: profile } = useProfile();
  const { data: status, isPending } = useMcpStatus();
  const { data: configured } = useTauriConfigured();
  const connect = useTauriConnect();
  const [copied, setCopied] = useState<string | null>(null);

  // The tick is a transient acknowledgement; a failure notice is not, since it
  // carries the only remaining instruction.
  useEffect(() => {
    if (!copied || copied === "error") return;
    const timer = setTimeout(() => setCopied(null), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!profile || isPending) return <Skeleton height="16rem" radius="lg" />;

  const copy = async (client: McpClient) => {
    try {
      await navigator.clipboard.writeText(
        client === "remote" ? MCP_CONNECTOR_URL : connectCommand(client, profile.id),
      );
      setCopied(client);
    } catch {
      setCopied("error");
    }
  };

  const desktop = isTauri();
  const anyLive = MCP_CLIENTS.some((c) => stateOf(status?.get(c)) === "live");

  return (
    <div className="flex flex-col gap-4 rounded-card border border-line bg-raised p-5">
      <div className="flex items-start gap-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-sunken text-ink-muted">
          <Sparkles className="h-5 w-5" />
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="text-base font-semibold">Claude &amp; Cursor</span>
          <span className="text-sm text-ink-muted">
            {anyLive
              ? "An assistant is connected and can read your notes, tasks, calendar and fridge."
              : "Let an AI assistant search your notes, tasks, calendar and fridge."}
          </span>
        </div>
      </div>

      {copied === "error" ? (
        <Alert variant="warning" title="Could not reach the clipboard">
          Copy the command from the box below by hand.
        </Alert>
      ) : null}
      {connect.error ? (
        <Alert variant="danger" title="Could not update the config">
          {String((connect.error as Error).message)}
        </Alert>
      ) : null}
      {desktop && configured && !configured.can_connect ? (
        <Alert variant="info" title="Run the terminal step once first">
          The app copies an existing entry rather than guessing where the server lives, so
          connect one editor from the Sebas repo to begin.
        </Alert>
      ) : null}

      <div className="flex flex-col gap-2">
        {MCP_CLIENTS.map((client) => {
          const conn = status?.get(client);
          const state = stateOf(conn);
          // Claude Code registers through its own CLI, so the app never writes
          // its config — only the two plain JSON ones.
          const remote = client === "remote";
          const writable =
            !remote && desktop && WRITABLE_CLIENTS.includes(client) && configured?.can_connect;
          const isConfigured = configured?.configured.includes(client) ?? false;

          return (
            <div
              key={client}
              className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-line px-4 py-3"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className={cn("h-2 w-2 shrink-0 rounded-full", DOT[state])} aria-hidden />
                <div className="flex min-w-0 flex-col">
                  <span className="text-sm font-semibold">{MCP_CLIENT_LABELS[client]}</span>
                  <span className="text-xs text-ink-faint">
                    {state === "live"
                      ? `Connected · active ${since(conn!.lastSeenAt)}`
                      : state === "stale"
                        ? `Last seen ${since(conn!.lastSeenAt)} — open the app to reconnect`
                        : remote
                          ? "Add as a connector on claude.ai, then use it anywhere"
                          : isConfigured
                            ? "Configured — restart the app to connect"
                            : "Not connected"}
                  </span>
                </div>
              </div>

              {writable ? (
                <Button
                  variant={isConfigured ? "outline" : "primary"}
                  size="sm"
                  disabled={connect.isPending}
                  onClick={() =>
                    connect.mutate({ client, userId: profile.id, disconnect: isConfigured })
                  }
                >
                  {connect.isPending ? "Working…" : isConfigured ? "Disconnect" : "Connect"}
                </Button>
              ) : (
                <Button
                  variant={copied === client ? "primary" : "secondary"}
                  size="sm"
                  className="gap-2"
                  onClick={() => void copy(client)}
                >
                  {copied === client ? (
                    <>
                      <Check className="h-4 w-4" aria-hidden />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" aria-hidden />
                      {remote ? "Copy URL" : "Copy command"}
                    </>
                  )}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col gap-2 border-t border-line pt-4">
        <span className="text-xs text-ink-faint">
          {desktop
            ? "Connect writes the editor's config; restart it afterwards. Claude Code registers through its own CLI, so it stays a command."
            : "Run the command in the Sebas repo, then restart the editor. Your user id is filled in; no connection string is stored in the editor's config."}
        </span>
        <code className="overflow-x-auto whitespace-pre rounded-card bg-sunken px-3 py-2 font-mono text-xs text-ink-muted">
          {connectCommand("claude-code", profile.id)}
        </code>
      </div>
    </div>
  );
}
